import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { Checkbox, Spin, Alert, Input, Tag, Tooltip, Button, message } from "antd";
import { CarOutlined, ToolOutlined, EyeOutlined, CopyOutlined, CheckOutlined } from "@ant-design/icons";
import { API_DOMAIN } from "../constants";
import { fetchMiscOptions, loadSideLabels, sideLabel, engineLayoutLabel, transmissionTypeLabel, drivetrainLabel, useQv4Theme } from "./helpers";
import { WaText } from "./MessageParts";

// Vehicle fields selectable for the supplier message — only non-empty ones render
const VEHICLE_FIELD_DEFS = [
  { key: "brand",          label: "Brand",        get: v => v.brand_name || v.brand },
  { key: "model",          label: "Model",        get: v => v.model_name || v.model },
  { key: "year",           label: "Year",         get: v => v.year },
  { key: "variant",        label: "Variant",      get: v => v.variant },
  { key: "vin",            label: "VIN",          get: v => v.vin },
  { key: "engine_code",    label: "Engine Code",  get: v => v.engine_code },
  { key: "engine_displacement", label: "Engine Displacement", get: v => v.engine_displacement },
  { key: "engine_layout",  label: "Engine Layout", get: v => v.engine_layout },
  { key: "transmission_type", label: "Transmission", get: v => v.transmission_type },
  { key: "transmission_code", label: "Transmission Code", get: v => v.transmission_code },
  { key: "drivetrain",     label: "Drivetrain",   get: v => v.drivetrain },
  { key: "fuel_type",      label: "Fuel Type",    get: v => v.fuel_type },
  { key: "country_spec",   label: "Spec",         get: v => v.country_spec },
  { key: "trim_info",      label: "Trim",         get: v => v.trim_info },
];

// Always included in the supplier message — checked, locked, not removable
const MANDATORY_VEHICLE_FIELDS = new Set(["brand", "model", "year", "vin", "country_spec"]);

const PART_FIELD_DEFS = [
  { key: "side",        label: "Side",      get: p => p.side },
  { key: "quantity",    label: "Quantity",  get: p => p.quantity },
  { key: "condition",   label: "Condition", get: p => p.condition_preference },
  { key: "part_number", label: "Part #",    get: p => p.part_number },
];

const hasValue = (v) => v !== null && v !== undefined && String(v).trim() !== "";

// WATI rejects a template send outright if any parameter value contains a
// tab, a newline, or 4+ consecutive spaces — mirrors the same restriction
// the backend already enforces server-side for every other template param.
function sanitizeAdditionalInfo(raw) {
  return raw
    .replace(/[\t\n\r]/g, " ")
    .replace(/ {4,}/g, "   ");
}

// Builds the selection payload sent to the preview and send APIs:
// { vehicle_fields: ["brand", ...], parts: [{ query_part_id, fields: ["side", ...] }], additional_info: "..." }
function buildSelection(vehicleChecked, partState, additionalInfo, imageIds) {
  return {
    vehicle_fields: [...vehicleChecked],
    parts: Object.entries(partState)
      .filter(([, s]) => s.included)
      .map(([id, s]) => ({ query_part_id: Number(id), fields: [...s.fields] })),
    additional_info: (additionalInfo || "").trim(),
    ...(imageIds?.length ? { image_ids: imageIds } : {}),
  };
}

// All the selection state + the live server-side preview it drives, with no
// opinion on layout — SendToSuppliersModal calls this once and renders the
// two halves (SupplierSelectionPanel / SupplierPreviewPanel) in whichever
// columns its layout wants, instead of them being stuck together in one
// component's JSX.
//
// `imageIds` — undefined/empty means "every photo", matching
// send_to_suppliers_v2's own default; a non-empty array is the exact set of
// selected photos' stable identifiers (raw stored path strings today — see
// SendToSuppliersModal's imageIdentifier). Passed in from the parent, which
// owns the photo-selection checkboxes, so the live preview (and the count
// baked into its text) actually reflects what's currently checked instead
// of always describing every photo on the query.
export function useSupplierMessageComposer({ open, taskId, task, isInternational, imageIds, onSelectionChange }) {
  const vehicle = task?.vehicle || {};
  const parts = task?.query_parts || [];

  const availableVehicleFields = useMemo(
    () => VEHICLE_FIELD_DEFS.filter(d => hasValue(d.get(vehicle))),
    [vehicle]
  );

  const [vehicleChecked, setVehicleChecked] = useState(new Set());
  const [additionalInfo, setAdditionalInfo] = useState("");
  const [partState, setPartState] = useState({}); // { [query_part_id]: { included, fields: Set } }
  const [conditionMap, setConditionMap] = useState({});
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewUnavailable, setPreviewUnavailable] = useState(false);
  const debounceRef = useRef(null);
  const initedForTaskRef = useRef(null);

  useEffect(() => {
    fetchMiscOptions("part_condition_origin").then(list => {
      setConditionMap(Object.fromEntries(list.map(o => [String(o.value), o.label])));
    });
  }, []);

  // sideLabel() reads a shared module-level cache (helpers.js), not React
  // state, so a re-render has to be forced once it's populated — same
  // pattern WorkflowTaskDrawer.js already uses for the same helper.
  const [, setSideLabelsReady] = useState(false);
  useEffect(() => { loadSideLabels().then(() => setSideLabelsReady(v => !v)); }, []);

  // Defaults when the task arrives: every available vehicle field on (staff
  // uncheck what they don't want to send); parts fully on
  //
  // This hook is called unconditionally (not gated behind "task fully
  // loaded"), so the very first render(s) can see an incomplete `task` —
  // e.g. the kanban card's own prop, before SendToSuppliersModal's own
  // fetch of the full task (with real query_parts) resolves. Waiting for
  // parts.length > 0 before marking initedForTaskRef means that premature
  // pass is skipped entirely rather than locking in an empty partState that
  // the real data then had no way to overwrite (the ref-by-taskId guard
  // would otherwise treat the premature pass as "already done").
  useEffect(() => {
    if (!open || !task || parts.length === 0 || initedForTaskRef.current === taskId) return;
    initedForTaskRef.current = taskId;
    setVehicleChecked(new Set(availableVehicleFields.map(d => d.key)));
    setAdditionalInfo("");
    // A part that's already been quoted by at least one supplier doesn't
    // need to go out again by default — this message is normally for
    // sourcing NEW parts (or re-asking ones nobody's answered yet). Staff
    // can still check it back on manually if they genuinely want to re-ask.
    setPartState(Object.fromEntries(parts.map(p => [
      p.id ?? p.query_part_id,
      {
        included: !(p.quotes?.length > 0),
        fields: new Set(PART_FIELD_DEFS.filter(d => hasValue(d.get(p))).map(d => d.key)),
      },
    ])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, task, taskId]);

  useEffect(() => {
    if (!open) { initedForTaskRef.current = null; setPreview(null); setPreviewUnavailable(false); }
  }, [open]);

  // Debounced server-side preview — same builder the send uses
  useEffect(() => {
    if (!open || !taskId || Object.keys(partState).length === 0) return;
    const selection = buildSelection(vehicleChecked, partState, additionalInfo, imageIds);
    onSelectionChange?.(selection);

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const res = await axios.post(
          `${API_DOMAIN}/query_workflow/send_to_suppliers_v2/preview`,
          { task_id: taskId, is_international: isInternational, selection },
          { headers: { accessToken: localStorage.getItem("accessToken") } }
        );
        if (res.data?.success && res.data.data?.message != null) {
          setPreview(res.data.data.message);
          setPreviewUnavailable(false);
        } else {
          setPreviewUnavailable(true);
        }
      } catch {
        setPreviewUnavailable(true);
      } finally {
        setPreviewLoading(false);
      }
    }, 450);

    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, taskId, vehicleChecked, partState, additionalInfo, isInternational, imageIds]);

  const toggleVehicleField = (key) => {
    if (MANDATORY_VEHICLE_FIELDS.has(key)) return; // locked
    setVehicleChecked(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const togglePart = (id) => {
    setPartState(prev => ({ ...prev, [id]: { ...prev[id], included: !prev[id]?.included } }));
  };

  const togglePartField = (id, key) => {
    setPartState(prev => {
      const cur = prev[id];
      const fields = new Set(cur.fields);
      fields.has(key) ? fields.delete(key) : fields.add(key);
      return { ...prev, [id]: { ...cur, fields } };
    });
  };

  const allVehicleChecked = availableVehicleFields.length > 0 && availableVehicleFields.every(d => vehicleChecked.has(d.key));

  const partFieldDisplay = (def, p) => {
    const raw = def.get(p);
    if (def.key === "condition") return conditionMap[String(raw)] ?? raw;
    if (def.key === "side") return sideLabel(raw);
    return raw;
  };

  // engine_layout/transmission_type/drivetrain are coded values (e.g.
  // "inline-4") same as side/condition — checkbox labels used to show the
  // raw code instead of a human label ("Inline-4").
  const vehicleFieldDisplay = (def) => {
    const raw = def.get(vehicle);
    if (def.key === "engine_layout") return engineLayoutLabel(raw);
    if (def.key === "transmission_type") return transmissionTypeLabel(raw);
    if (def.key === "drivetrain") return drivetrainLabel(raw);
    return raw;
  };

  return {
    vehicle, parts,
    availableVehicleFields, allVehicleChecked, vehicleChecked, setVehicleChecked, toggleVehicleField, vehicleFieldDisplay,
    additionalInfo, setAdditionalInfo,
    partState, togglePart, togglePartField, partFieldDisplay,
    preview, previewLoading, previewUnavailable,
  };
}

// Vehicle + parts checkboxes — "what to include" side of the composer.
export function SupplierSelectionPanel({ composer }) {
  const { t } = useQv4Theme();
  const {
    vehicle, parts, availableVehicleFields, allVehicleChecked, vehicleChecked, setVehicleChecked, toggleVehicleField, vehicleFieldDisplay,
    additionalInfo, setAdditionalInfo, partState, togglePart, togglePartField, partFieldDisplay,
  } = composer;

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, gap: 4 }}>
      {/* Vehicle — fixed, doesn't scroll; only the Parts list below does */}
      <div style={{ flexShrink: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
        <CarOutlined style={{ color: "#1677ff", fontSize: 13 }} />
        <span style={{ fontSize: 12, fontWeight: 700, color: t.textSub }}>Vehicle details</span>
        <div style={{ flex: 1 }} />
        <Checkbox
          checked={allVehicleChecked}
          indeterminate={vehicleChecked.size > 0 && !allVehicleChecked}
          onChange={() => setVehicleChecked(allVehicleChecked
            // "Deselect all" still keeps the mandatory fields
            ? new Set(availableVehicleFields.filter(d => MANDATORY_VEHICLE_FIELDS.has(d.key)).map(d => d.key))
            : new Set(availableVehicleFields.map(d => d.key)))}
        >
          <span style={{ fontSize: 11 }}>All</span>
        </Checkbox>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2px 10px", marginBottom: 12 }}>
        {availableVehicleFields.map(d => {
          const locked = MANDATORY_VEHICLE_FIELDS.has(d.key);
          return (
            <Checkbox
              key={d.key}
              checked={vehicleChecked.has(d.key)}
              disabled={locked}
              onChange={() => toggleVehicleField(d.key)}
              style={{ fontSize: 12 }}
            >
              <span style={{ fontSize: 12, color: locked ? t.text : undefined }}>
                {d.label}: <strong>{String(vehicleFieldDisplay(d))}</strong>
                {locked && <span style={{ fontSize: 10, color: t.textSub, marginLeft: 4 }}>🔒</span>}
              </span>
            </Checkbox>
          );
        })}
      </div>
      <div style={{ marginBottom: 12 }}>
        <Input
          size="small"
          placeholder="Additional info for this query only (not saved to the vehicle)…"
          value={additionalInfo}
          onChange={e => setAdditionalInfo(sanitizeAdditionalInfo(e.target.value))}
          maxLength={200}
          style={{ fontSize: 12 }}
        />
      </div>
      </div>

      {/* Parts — the only part of this panel that scrolls internally */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6, flexShrink: 0 }}>
        <ToolOutlined style={{ color: t.textSub, fontSize: 13 }} />
        <span style={{ fontSize: 12, fontWeight: 700, color: t.textSub }}>Parts</span>
      </div>
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6, paddingRight: 4 }}>
        {parts.map(p => {
          const id = p.id ?? p.query_part_id;
          const st = partState[id];
          if (!st) return null;
          const availableFields = PART_FIELD_DEFS.filter(d => hasValue(d.get(p)));
          return (
            <div key={id} style={{
              border: `1px solid ${st.included ? "#d6e4ff" : t.border}`,
              background: st.included ? "#f7faff" : t.headerBg,
              borderRadius: 8, padding: "6px 10px",
              opacity: st.included ? 1 : 0.6,
            }}>
              <Checkbox checked={st.included} onChange={() => togglePart(id)}>
                <span style={{ fontSize: 12, fontWeight: 600 }}>{p.part_name || `Part #${id}`}</span>
              </Checkbox>
              {p.quotes?.length > 0 && (
                <Tooltip title="Already quoted by at least one supplier — unchecked by default, but you can re-ask">
                  <Tag color="green" style={{ marginLeft: 4, fontSize: 10, padding: "0 5px", lineHeight: "16px" }}>
                    {p.quotes.length} quote{p.quotes.length > 1 ? "s" : ""}
                  </Tag>
                </Tooltip>
              )}
              {st.included && availableFields.length > 0 && (
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 2, paddingLeft: 24 }}>
                  {availableFields.map(d => (
                    <Checkbox
                      key={d.key}
                      checked={st.fields.has(d.key)}
                      onChange={() => togglePartField(id, d.key)}
                    >
                      <span style={{ fontSize: 11, color: t.textSub }}>
                        {d.label}: {String(partFieldDisplay(d, p))}
                      </span>
                    </Checkbox>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Read-only "what the supplier sees" preview, styled as the same outbound
// WhatsApp bubble used elsewhere (asymmetric corners, shadow, "sent" tick
// footer — matches the bulk-template preview in Modals.js) rather than the
// plain rounded box this used to be.
export function SupplierPreviewPanel({ composer }) {
  const { t } = useQv4Theme();
  const { preview, previewLoading, previewUnavailable } = composer;
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!preview) return;
    try {
      await navigator.clipboard.writeText(preview);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      message.error("Failed to copy — your browser may be blocking clipboard access");
    }
  };

  return (
    <div style={{ height: "100%", minHeight: 0, display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6, flexShrink: 0 }}>
        <EyeOutlined style={{ color: t.textSub, fontSize: 13 }} />
        <span style={{ fontSize: 12, fontWeight: 700, color: t.textSub }}>Supplier's view</span>
        {previewLoading && <Spin size="small" style={{ marginLeft: 4 }} />}
        <div style={{ flex: 1 }} />
        {preview != null && (
          <Tooltip title={copied ? "Copied!" : "Copy message"}>
            <Button
              size="small" type="text"
              icon={copied ? <CheckOutlined style={{ color: "#52c41a" }} /> : <CopyOutlined />}
              onClick={handleCopy}
              style={{ padding: "0 6px" }}
            />
          </Tooltip>
        )}
      </div>
      <div style={{
        flex: 1, minHeight: 120, overflowY: "auto",
        background: "#efeae2", borderRadius: 10, padding: 12,
      }}>
        {previewUnavailable ? (
          <Alert
            type="info"
            showIcon
            style={{ fontSize: 12 }}
            message="Live preview not available yet"
            description="The message will be built server-side from your selection when sent. The preview API isn't deployed yet."
          />
        ) : preview != null ? (
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <div style={{
              maxWidth: "92%", background: "#d9fdd3", borderRadius: "12px 4px 12px 12px",
              overflow: "hidden", boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
            }}>
              <div style={{ padding: "6px 10px 2px" }}>
                <div style={{ fontSize: 13, whiteSpace: "pre-wrap", lineHeight: 1.6, color: "#111", wordBreak: "break-word" }}>
                  <WaText text={preview} />
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 2, marginBottom: 2 }}>
                  <span style={{ fontSize: 10, color: "#667781" }}>now ✓✓</span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div style={{ fontSize: 12, color: t.textSub, textAlign: "center", paddingTop: 30 }}>
            {previewLoading ? "Building preview…" : "Preview appears here"}
          </div>
        )}
      </div>
    </div>
  );
}
